import { useQuery } from "@apollo/client";
import { Box, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import styled from "styled-components";

import {
  CallPlanInfoFragment,
  GetCallPlanActivitiesQuery,
  GetCallPlanActivitiesQueryVariables,
} from "@/graphql/graphql";
import { Label, WeekDetails, WeekOverview } from "@/pages/UserDetails/styles";
import { transformDate } from "@/utils/date-utils";

import ActivityDialog from "../CallPlans/CreateCallPlanModal/ActivityDialog/ActivityDialog";
import { GET_CALL_PLAN_ACTIVITIES_QUERY } from "../CallPlans/CreateCallPlanModal/CreateCallPlanModal.queries";
import { CallPlanActivityInteraction } from "../CallPlans/CreateCallPlanModal/CreateCallPlanModal.utils";

type CallPlanCurrentWeekOverviewProps = {
  activeCallPlan: CallPlanInfoFragment;
};

const hbaTitleSx = {
  maxWidth: 175,
  fontSize: "14.7px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const hbaTitleInteractiveSx = {
  ...hbaTitleSx,
  color: "primary.main",
  textDecoration: "underline",
} as const;

const ActivityDetailsCell = styled(WeekDetails)`
  cursor: pointer;
  height: 40px;
`;

const CallPlanCurrentWeekOverview = ({ activeCallPlan }: CallPlanCurrentWeekOverviewProps) => {
  const [activityDialogActivityId, setActivityDialogActivityId] = useState<number | null>(null);

  const { data: callPlanActivitiesData, loading: callPlanActivitiesLoading } = useQuery<
    GetCallPlanActivitiesQuery,
    GetCallPlanActivitiesQueryVariables
  >(GET_CALL_PLAN_ACTIVITIES_QUERY, {
    variables: { callPlanId: activeCallPlan.id },
    fetchPolicy: "cache-and-network",
  });

  const activities = useMemo(() => {
    const interactions = callPlanActivitiesData?.callPlan.interactions;
    if (!interactions?.length) {
      return [];
    }

    return interactions
      .filter((i): i is CallPlanActivityInteraction => i.__typename === "ActivityInteraction")
      .sort((a, b) => a.order - b.order);
  }, [callPlanActivitiesData?.callPlan?.interactions]);

  return (
    <>
      <WeekOverview>
        <WeekDetails>
          <Label>Week</Label>
          {String(activeCallPlan.programWeek)}
        </WeekDetails>
        <WeekDetails>
          <Label>Start date:</Label>
          {transformDate(activeCallPlan.startDate)}
        </WeekDetails>
        <WeekDetails>
          <Label>End date:</Label>
          {activeCallPlan.endDate ? transformDate(activeCallPlan.endDate) : "NR"}
        </WeekDetails>
      </WeekOverview>
      <WeekOverview>
        {callPlanActivitiesLoading && (
          <WeekDetails>
            <Label> HBA 1</Label>
            <Box component="span" sx={hbaTitleSx}>
              Loading...
            </Box>
          </WeekDetails>
        )}
        {!callPlanActivitiesLoading &&
          activities.map((interaction, index) => {
            const titleText = interaction.title ? interaction.title : "NR";

            return (
              <ActivityDetailsCell
                key={interaction.id}
                role="button"
                tabIndex={0}
                onClick={() => setActivityDialogActivityId(interaction.activityId ?? null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActivityDialogActivityId(interaction.activityId ?? null);
                  }
                }}
              >
                <Label> HBA {index + 1}</Label>
                <Tooltip
                  title={<Typography fontSize={14}>{titleText}</Typography>}
                  placement="top"
                  arrow
                >
                  <Box component="span" sx={hbaTitleInteractiveSx}>
                    {titleText}
                  </Box>
                </Tooltip>
              </ActivityDetailsCell>
            );
          })}
      </WeekOverview>

      <ActivityDialog
        activityId={activityDialogActivityId}
        onClose={() => setActivityDialogActivityId(null)}
      />
    </>
  );
};

export default CallPlanCurrentWeekOverview;
